<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019 (original work) Open Assessment Technologies SA ;
    import { __ } from '@oat-sa-private/ui-core';
    import { createEventDispatcher } from 'svelte';
    import Icon from '../icon/Icon.svelte';

    const types = {
        button: 'button',
        submit: 'submit',
        reset: 'reset'
    };
    const skins = {
        primary: 'primary',
        secondary: 'secondary',
        'visually-disabled': 'visually-disabled'
    };
    const shapes = {
        pill: 'pill',
        circular: 'circular'
    };
    const sizes = {
        small: 'small',
        medium: 'medium',
        large: 'large'
    };

    export let label = __('button');
    export let name = null;
    export let ariaLabel = false;
    export let type = types.button;
    export let skin = skins.primary;
    export let inverted = false;
    export let shape = false;
    export let size = 'medium';
    export let fullwidth = false;
    export let icon = false;
    export let iconSide = 'left';
    export let divider = false;
    export let iconRtlFlip = false;
    export let disabled = false;
    export let ariaLabelledBy;
    export let ariaDescribedBy;
    export let ariaHasPopup;
    export let ariaControls;
    export let dataTestId = null;
    export let form = null;

    if (!types[type]) {
        type = types.button;
    }
    if (!skins[skin]) {
        skin = skins.primary;
    }
    if (!shapes[shape]) {
        shape = '';
    }
    if (!sizes[size]) {
        size = sizes.medium;
    }

    $: isCircular = shape === shapes.circular;

    const dispatch = createEventDispatcher();
    /**
     * Formats event data and re-dispatches event
     * It's called for click and keydown events:
     * on keydown it's called for logging purposes,
     * as we need to provide data about pressed key value
     *
     * @param {Event} event
     */
    const handleClick = event => {
        const domEventType = event.type;
        const pressedKey = event.key;
        const target = event.target;
        const isClickEvent = domEventType === 'click' || pressedKey === 'Enter' || pressedKey === ' ';

        let position;
        if (event instanceof MouseEvent) {
            position = {
                clientX: event.clientX,
                clientY: event.clientY,
                screenX: event.screenX,
                screenY: event.screenY
            };
        }

        if (isClickEvent) {
            dispatch('click', { event, domEventType, pressedKey, target, ...(position && { position }) });
        }
    };
</script>

<style>
    /* Inherits actionable styles via the .actionable class & inherited mixins */
    button {
        --height-small: 5rem;
        --height-medium: 6rem;
        --height-large: 8rem;
        --width-small: 10rem;
        --width-medium: 12rem;
        --width-large: 16rem;
        --min-hitbox: 5.5rem;

        position: relative;
        vertical-align: middle;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        border-radius: var(--radius-medium);
        font-family: var(--font-ui);
        font-size: var(--fontsize-heading);
        @add-mixin bold;
        text-transform: uppercase;
        white-space: nowrap;
        block-size: var(--height-medium);
        padding-block: 0;
        padding-inline: var(--space-2x);
        /* necessary to prevent focus outline touching neighbours */
        margin-block: var(--space-half);
        margin-inline: var(--space-half);
        min-inline-size: var(--width-medium);
        letter-spacing: inherit;
        word-spacing: inherit;

        /* Size variants */
        &.small {
            font-size: var(--fontsize-body-xs);
            block-size: var(--height-small);
            padding-block: 0;
            padding-inline: var(--space-1x5);
            min-inline-size: var(--width-small);
        }
        &.large {
            block-size: var(--height-large);
            padding-block: 0;
            padding-inline: var(--space-3x);
            min-inline-size: var(--width-large);
        }
        /* Fullwidth variant */
        &.fullwidth {
            @add-mixin fullwidth;
        }
        /* Pill variant */
        &.pill {
            border-radius: var(--radius-pill);
            &:focus-visible::after {
                border-radius: var(--radius-pill);
            }
        }

        & .divider {
            width: 1px;
            height: 70%;
            border-right: 1px solid;
        }
        /* Round variant (icon only) */
        &.circular {
            block-size: var(--height-medium);
            min-inline-size: var(--height-medium);
            padding: 0;
            border-radius: var(--radius-circular);
            &:focus-visible::after {
                border-radius: var(--radius-circular);
            }
            &.small {
                block-size: var(--height-small);
                min-inline-size: var(--height-small);
            }
            &.large {
                block-size: var(--height-large);
                min-inline-size: var(--height-large);
            }
        }
        /* With Icon + Text inside */
        &:not(.circular) {
            &:not(.icon-right) {
                flex-direction: row;
                & > :global(svg) {
                    margin-inline-end: var(--space-1x5);
                }

                & .divider {
                    margin-inline-end: 1rem;
                }
            }
            &.icon-right {
                flex-direction: row-reverse;
                & > :global(svg) {
                    margin-inline-start: var(--space-1x5);
                }

                & .divider {
                    margin-inline-start: 1rem;
                }
            }
        }

        /* Focus styles */
        @add-mixin outline-focus -1rem;
        &:focus-visible::after {
            border-radius: var(--radius-large);
        }

        &:before {
            --offset: calc(100% - var(--min-hitbox)) / 2 - var(--border-medium);
            --hitbox-size: calc(100% + var(--border-medium) * 2);

            position: absolute;
            inset-block-start: var(--offset);
            inset-inline-start: var(--offset);

            inline-size: var(--hitbox-size);
            block-size: var(--hitbox-size);
            min-block-size: var(--min-hitbox);
            min-inline-size: var(--min-hitbox);

            content: '';
        }
    }
    /* Inverted focus */
    :global(.inverted) button:focus-visible::after,
    button.inverted:focus-visible::after {
        border-color: var(--color-border-focus-inverted);
    }
</style>

<button
    {type}
    {name}
    class="actionable {skin} {shape} {size}"
    class:inverted
    class:fullwidth
    class:icon-right={iconSide === 'right'}
    {disabled}
    {form}
    on:keydown
    on:click={handleClick}
    data-test-id={dataTestId ? dataTestId : void 0}
    aria-label={ariaLabel ? ariaLabel : null}
    aria-labelledby={ariaLabelledBy ? ariaLabelledBy : null}
    aria-describedBy={ariaDescribedBy ? ariaDescribedBy : void 0}
    aria-haspopup={ariaHasPopup}
    aria-controls={ariaControls}
    aria-disabled={disabled ? disabled : null}>
    {#if icon}
        <Icon name={icon} ariaHidden={ariaLabel !== false} rtlFlip={iconRtlFlip} />
    {/if}
    {#if divider}
        <span class="divider" />
    {/if}
    {#if !isCircular}{label}{/if}
</button>
